/** Shared widgets for the Transaction Wizard designer. The styles are the
    General Process designer's (one look across the designers); this adds the
    small controls a step inspector is made of. */

import type React from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import {
  Field, SectionHeader, cardStyle, emptyHintStyle, hintStyle, inputStyle,
  monoAreaStyle, selectStyle, tdStyle, thStyle,
} from '../genprocess/ui'

export {
  Field, SectionHeader, cardStyle, emptyHintStyle, hintStyle, inputStyle,
  monoAreaStyle, selectStyle, tdStyle, thStyle,
}

export const monoInputStyle: React.CSSProperties = {
  ...inputStyle, fontFamily: 'monospace', fontSize: 13,
}

export const smallBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
  fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)', cursor: 'pointer',
}

export const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, border: 'none', borderRadius: 4, background: 'transparent',
  color: 'var(--color-text-secondary)', cursor: 'pointer', flexShrink: 0,
}

/** A two-column grid of fields. */
export function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 12px' }}>
      {children}
    </div>
  )
}

export function TextInput({
  value, onChange, placeholder, mono, disabled, style,
}: {
  value: string | undefined | null
  onChange: (v: string) => void
  placeholder?: string
  mono?: boolean
  disabled?: boolean
  style?: React.CSSProperties
}) {
  return (
    <input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck={false}
      style={{ ...(mono ? monoInputStyle : inputStyle), ...style }}
    />
  )
}

export function NumberInput({
  value, onChange, placeholder, min,
}: {
  value: number | undefined | null
  onChange: (v: number | undefined) => void
  placeholder?: string
  min?: number
}) {
  return (
    <input
      type="number"
      value={value ?? ''}
      min={min}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      style={inputStyle}
    />
  )
}

export interface Option { value: string; label: string; disabled?: boolean }

export function SelectInput({
  value, options, onChange, empty, disabled,
}: {
  value: string | undefined | null
  options: Array<Option | string>
  onChange: (v: string) => void
  /** Label of a leading blank option (omit for none). */
  empty?: string
  disabled?: boolean
}) {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
  const v = value ?? ''
  const known = v === '' || opts.some((o) => o.value === v)
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)} style={selectStyle} disabled={disabled}>
      {empty !== undefined && <option value="">{empty}</option>}
      {/* A value no option knows (a hand-edited model) is shown, not lost. */}
      {!known && <option value={v}>{v} (not found)</option>}
      {opts.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
    </select>
  )
}

export function Check({
  checked, onChange, label, hint,
}: { checked: boolean | undefined; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13.5, cursor: 'pointer', marginBottom: 8 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <span>
        {label}
        {hint && <div style={hintStyle}>{hint}</div>}
      </span>
    </label>
  )
}

export function Badge({ children, tone = 'neutral', title }: {
  children: React.ReactNode
  tone?: 'neutral' | 'warn' | 'error' | 'accent' | 'ok'
  title?: string
}) {
  const color = tone === 'error' ? 'var(--color-error, #d33)'
    : tone === 'warn' ? 'var(--color-warning, #b45309)'
      : tone === 'accent' ? 'var(--color-accent)'
        : tone === 'ok' ? 'var(--color-success, #15803d)'
          : 'var(--color-text-secondary)'
  return (
    <span title={title} style={{
      display: 'inline-block', fontSize: 13, lineHeight: '14px', padding: '0 5px',
      borderRadius: 7, border: `1px solid ${color}`, color, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

/** Remove-row button. */
export function RemoveBtn({ onClick, title = 'Remove' }: { onClick: () => void; title?: string }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} style={iconBtn}>
      <Trash2 size={13} />
    </button>
  )
}

export function AddBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={smallBtn}>
      <Plus size={12} /> {children}
    </button>
  )
}

/** An editable list of short strings (chips + an add box). */
export function StringList({
  values, onChange, suggestions, placeholder,
}: {
  values: string[] | undefined
  onChange: (v: string[]) => void
  suggestions?: string[]
  placeholder?: string
}) {
  const list = values || []
  const remaining = (suggestions || []).filter((s) => !list.includes(s))
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: list.length ? 6 : 0 }}>
        {list.map((v, i) => (
          <span key={`${v}-${i}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 4px 1px 8px',
            fontSize: 13, borderRadius: 10, background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
          }}>
            {v}
            <button type="button" style={{ ...iconBtn, width: 16, height: 16 }} aria-label={`Remove ${v}`}
              onClick={() => onChange(list.filter((_, j) => j !== i))}>
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {remaining.length > 0 && (
          <select value="" onChange={(e) => { if (e.target.value) onChange([...list, e.target.value]) }}
            style={{ ...selectStyle, flex: 1 }}>
            <option value="">Add…</option>
            {remaining.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <input
          placeholder={placeholder || (remaining.length ? 'or type and press Enter' : 'Type and press Enter')}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            const v = (e.target as HTMLInputElement).value.trim()
            if (v && !list.includes(v)) onChange([...list, v])
            ;(e.target as HTMLInputElement).value = ''
          }}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
    </div>
  )
}

/** A small titled sub-section inside an inspector card. */
export function Sub({ title, right, children }: {
  title: string; right?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
      <SectionHeader right={right}>{title}</SectionHeader>
      {children}
    </div>
  )
}

/** Parse JSON typed into a textarea; null while it does not parse. */
export function parseJsonOrNull(text: string): unknown | null {
  const t = text.trim()
  if (!t) return undefined
  try { return JSON.parse(t) } catch { return null }
}
